import { useState } from 'react';
import type { TodoItem } from '../types';

interface Props {
  todos: TodoItem[];
  onToggle: (id: string) => void;
  onAdd: (text: string) => void;
  onRemove: (id: string) => void;
  onEdit: (id: string, text: string) => void;
}

export function TodoList({ todos, onToggle, onAdd, onRemove, onEdit }: Props) {
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(draft);
    setDraft('');
  };

  const startEdit = (t: TodoItem) => {
    setEditingId(t.id);
    setEditText(t.text);
  };

  const saveEdit = () => {
    if (editingId && editText.trim()) onEdit(editingId, editText.trim());
    setEditingId(null);
  };

  return (
    <section className="todo-panel">
      <header className="panel-header">
        <h3>Daily to-dos</h3>
        <span className="muted">
          {todos.filter((t) => t.done).length}/{todos.length} done
        </span>
      </header>

      <ul className="todo-list">
        {todos.length === 0 && (
          <li className="todo-empty">Nothing yet — add a checklist item.</li>
        )}
        {todos.map((t) => (
          <li key={t.id} className={`todo-item ${t.done ? 'done' : ''}`}>
            <label className="todo-check">
              <input
                type="checkbox"
                checked={t.done}
                onChange={() => onToggle(t.id)}
                aria-label={t.text}
              />
              <span className="checkmark" />
            </label>
            {editingId === t.id ? (
              <input
                className="todo-edit"
                value={editText}
                autoFocus
                onChange={(e) => setEditText(e.target.value)}
                onBlur={saveEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit();
                  if (e.key === 'Escape') setEditingId(null);
                }}
              />
            ) : (
              <button type="button" className="todo-text" onClick={() => startEdit(t)}>
                {t.text}
              </button>
            )}
            <button
              type="button"
              className="todo-remove"
              onClick={() => onRemove(t.id)}
              aria-label="Remove"
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <form className="todo-add" onSubmit={submit}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a to-do…"
          aria-label="New to-do"
        />
        <button type="submit" disabled={!draft.trim()}>
          Add
        </button>
      </form>
    </section>
  );
}
